import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import useLenis from "@/lib/useLenis";
import useSeo, { SEO_SITE_URL } from "@/lib/useSeo";
import { useLang } from "@/lib/LangContext";
import Configurator from "@/components/site/Configurator";
import Footer from "@/components/site/Footer";
import { caseStudies, caseOrder } from "@/lib/caseStudies";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function MinimalNav() {
  const { lang, setLang, t } = useLang();
  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/5" data-testid="start-nav">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 flex items-center justify-between h-16 lg:h-20">
        <Link to="/" data-testid="start-logo" className="flex items-center gap-2 group">
          <span className="w-2 h-2 rounded-full bg-red-500 shadow-[0_0_16px_rgba(239, 68, 68,0.8)] group-hover:scale-125 transition-transform" />
          <span className="font-display font-black tracking-tight text-[17px] uppercase">
            Emergent<span className="text-red-400">.</span>Food
          </span>
        </Link>
        <div className="flex items-center gap-4">
          <div className="hidden sm:flex items-center gap-1 text-xs font-mono-ef" data-testid="lang-switcher">
            <button data-testid="lang-it" onClick={() => setLang("it")}
              className={`px-2 py-1 rounded-full transition-colors ${lang === "it" ? "text-white bg-white/10" : "text-white/40 hover:text-white/80"}`}>IT</button>
            <span className="text-white/20">/</span>
            <button data-testid="lang-en" onClick={() => setLang("en")}
              className={`px-2 py-1 rounded-full transition-colors ${lang === "en" ? "text-white bg-white/10" : "text-white/40 hover:text-white/80"}`}>EN</button>
          </div>
          <Link to="/" data-testid="start-close" aria-label="Close" className="text-white/70 hover:text-white transition-colors flex items-center gap-2">
            <span className="hidden sm:inline text-xs font-mono-ef uppercase tracking-[0.2em]">{t.start.close}</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </Link>
        </div>
      </div>
    </header>
  );
}

function TrustBar({ t }) {
  return (
    <section className="py-16 lg:py-20 border-t border-white/10" data-testid="start-trust">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className="font-mono-ef text-[10px] uppercase tracking-[0.28em] text-white/40 mb-8 text-center">
          {t.start.trust_overline}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 items-center">
          {caseOrder.map((slug) => {
            const cs = caseStudies[slug];
            return (
              <a
                key={slug}
                href={cs.external_url || "#"}
                target={cs.external_url ? "_blank" : undefined}
                rel={cs.external_url ? "noopener noreferrer" : undefined}
                data-testid={`start-trust-${slug}`}
                className="font-display text-xl lg:text-2xl font-bold text-white/60 hover:text-white transition-colors text-center py-4 border-y border-white/10 tracking-tight"
              >
                {cs.logo}
              </a>
            );
          })}
          <div className="font-mono-ef text-[10px] uppercase tracking-[0.22em] text-white/35 text-center py-4 border-y border-white/10">
            Riservato NDA
          </div>
          <div className="font-mono-ef text-[10px] uppercase tracking-[0.22em] text-white/35 text-center py-4 border-y border-white/10">
            Riservato NDA
          </div>
          <div className="font-mono-ef text-xs uppercase tracking-[0.16em] text-white/40 text-center py-4 border-y border-white/10">
            {t.start.trust_more}
          </div>
        </div>
      </div>
    </section>
  );
}

function NextSteps({ t }) {
  return (
    <section className="py-24 lg:py-32 border-t border-white/10" data-testid="start-next-steps">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14 lg:mb-16">
          <div className="lg:col-span-6">
            <div className="text-[11px] uppercase tracking-[0.28em] text-red-400 font-mono-ef">— Process</div>
            <h2 className="mt-6 font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-[-0.035em]">
              {t.start.next_title}
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 lg:pt-4">
            <p className="text-lg text-white/60 leading-relaxed">{t.start.next_sub}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.start.next_steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.08, duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
              className="card-elev rounded-2xl p-8 min-h-[240px] flex flex-col justify-between"
              data-testid={`next-step-${s.n}`}
            >
              <div className="chapter-num text-5xl lg:text-6xl">{s.n}</div>
              <div>
                <h3 className="font-display text-xl lg:text-2xl font-bold tracking-tight text-white">
                  {s.t}
                </h3>
                <p className="mt-3 text-sm text-white/60 leading-relaxed">{s.d}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonial({ t }) {
  return (
    <section className="py-24 lg:py-32 border-t border-white/10 bg-[#070708]" data-testid="start-testimonial">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 text-center">
        <div className="font-mono-ef text-[10px] uppercase tracking-[0.28em] text-red-400 mb-8">
          {t.start.testimonial_overline}
        </div>
        <p className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-[-0.02em] font-bold max-w-4xl mx-auto">
          "{t.start.testimonial_quote}"
        </p>
        <div className="mt-8 text-sm text-white/50 font-mono-ef uppercase tracking-[0.2em]">
          {t.start.testimonial_author}
        </div>
      </div>
    </section>
  );
}

function CalendlyInline({ url, embedUrl, t }) {
  useEffect(() => {
    if (!embedUrl) return;
    if (document.getElementById("calendly-widget-script")) return;
    const s = document.createElement("script");
    s.id = "calendly-widget-script";
    s.src = "https://assets.calendly.com/assets/external/widget.js";
    s.async = true;
    document.body.appendChild(s);
  }, [embedUrl]);

  useEffect(() => {
    if (!embedUrl) return;
    const l = document.getElementById("calendly-widget-css");
    if (!l) {
      const link = document.createElement("link");
      link.id = "calendly-widget-css";
      link.rel = "stylesheet";
      link.href = "https://assets.calendly.com/assets/external/widget.css";
      document.head.appendChild(link);
    }
  }, [embedUrl]);

  if (!url) return null;

  return (
    <section className="py-24 lg:py-32 border-t border-white/10" data-testid="start-calendly">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className={`grid grid-cols-1 gap-10 ${embedUrl ? "lg:grid-cols-12" : ""}`}>
          <div className={embedUrl ? "lg:col-span-5" : "max-w-3xl mx-auto text-center"}>
            <div className="text-[11px] uppercase tracking-[0.28em] text-red-400 font-mono-ef">
              — {t.start.calendly_overline}
            </div>
            <h2 className="mt-6 font-display font-black text-5xl lg:text-6xl leading-[0.92] tracking-[-0.035em]">
              {t.start.calendly_title}
            </h2>
            <p className={`mt-6 text-lg text-white/60 leading-relaxed ${embedUrl ? "max-w-lg" : "max-w-2xl mx-auto"}`}>
              {t.start.calendly_sub}
            </p>
            <a
              href={url}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="start-calendly-external"
              className="mt-8 inline-flex btn-pill btn-primary"
            >
              {t.start.calendly_cta}
              <svg className="ml-2" width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M7 17L17 7M17 7H9M17 7v8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>
          {embedUrl && (
            <div className="lg:col-span-7">
              <div
                className="calendly-inline-widget rounded-2xl overflow-hidden border border-white/10"
                data-url={`${embedUrl}?background_color=0a0a0a&text_color=f5f5f5&primary_color=10b981&hide_gdpr_banner=1`}
                style={{ minWidth: "320px", height: "640px" }}
                data-testid="start-calendly-embed"
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function MicroFaq({ t }) {
  return (
    <section className="py-24 lg:py-32 border-t border-white/10" data-testid="start-faq">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        <div className="text-center mb-14">
          <div className="text-[11px] uppercase tracking-[0.28em] text-red-400 font-mono-ef">
            — {t.start.faq_overline}
          </div>
          <h2 className="mt-6 font-display font-black text-5xl sm:text-6xl leading-[0.9] tracking-[-0.035em]">
            {t.start.faq_title}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {t.start.faq_items.map((f, i) => (
            <motion.div
              key={f.q}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06, duration: 0.6 }}
              className="border-t border-white/10 pt-6"
              data-testid={`start-faq-${i}`}
            >
              <div className="chapter-num text-4xl mb-4">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="font-display text-lg lg:text-xl font-bold tracking-tight">{f.q}</h3>
              <p className="mt-3 text-sm text-white/55 leading-relaxed">{f.a}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function StartProject() {
  useLenis();
  const { lang, t } = useLang();
  const [calendlyUrl, setCalendlyUrl] = useState("");
  const [calendlyEmbedUrl, setCalendlyEmbedUrl] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/config/public`)
      .then(({ data }) => {
        setCalendlyUrl(data?.calendly_url || "");
        setCalendlyEmbedUrl(data?.calendly_embed_url || "");
      })
      .catch(() => {});
  }, []);

  const title = lang === "en" ? "Start a project · Emergent Food" : "Inizia un progetto · Emergent Food";
  useSeo({
    title,
    description: t.start.subtitle,
    path: "/start",
    lang,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: title,
        description: t.start.subtitle,
        url: `${SEO_SITE_URL}/start`,
        isPartOf: { "@type": "WebSite", name: "Emergent Food", url: SEO_SITE_URL },
      },
    ],
  });

  return (
    <div className="App grain min-h-screen bg-[#050505] text-white relative" data-testid="start-page">
      <MinimalNav />

      <main className="relative">
        {/* Focused hero */}
        <section className="relative pt-32 lg:pt-44 pb-16 lg:pb-24" data-testid="start-hero">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-8">
                <div className="text-[11px] uppercase tracking-[0.28em] text-red-400 font-mono-ef">
                  — {t.start.overline}
                </div>
                <h1 className="mt-8 font-display font-black tracking-[-0.045em] leading-[0.88] text-[12vw] sm:text-[10vw] lg:text-[7.2vw]">
                  {t.start.title_lines.map((line, i) => (
                    <span
                      key={i}
                      className={`block ${i === t.start.title_lines.length - 1 ? "text-red-400" : ""}`}
                    >
                      {line}
                    </span>
                  ))}
                </h1>
              </div>
              <div className="lg:col-span-4 lg:pt-6 flex flex-col justify-end">
                <p className="text-lg lg:text-xl text-white/70 leading-relaxed">{t.start.subtitle}</p>
                <div className="mt-8 flex items-center gap-3 text-sm text-white/50">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                  </span>
                  <span className="font-mono-ef text-[10px] uppercase tracking-[0.24em] text-red-300">
                    {lang === "en" ? "Team currently online" : "Team attivo ora"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Configurator */}
        <Configurator />

        {/* Next steps */}
        <NextSteps t={t} />

        {/* Trust bar */}
        <TrustBar t={t} />

        {/* Testimonial */}
        <Testimonial t={t} />

        {/* Calendly section removed by request */}

        {/* Micro FAQ */}
        <MicroFaq t={t} />
      </main>

      <Footer />
    </div>
  );
}
